import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  FormLabel,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useEdc } from '../edc/EdcContext';
import {
  EDC_LINEUP,
  formatSetTime,
  getDayLabel,
} from '../edc/edcLineup';


export function SubmitScoreForm({ onSuccess, onCancel, prefill }) {
  const { edcMode } = useEdc();
  const [territory, setTerritory] = useState('');
  const [battleKey, setbattleKey] = useState('');
  const [score, setScore] = useState('');
  const [course, setCourse] = useState('Emerald');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [savedCourse, setSavedCourse] = useState(null);
  const [savedBattleKey, setSavedBattleKey] = useState(null);

  // The lineup record matching the current `territory` selection (if any).
  // In EDC mode, `territory` stores a set id (e.g. "fri-bass-pod-atliens")
  // so we can resolve straight back to artist/stage/time/day for display.
  const matchedSet = edcMode
    ? EDC_LINEUP.find((s) => s.id === territory)
    : null;

  // Apply prefill (from a popped bubble or "I'm Going" button click) every
  // time the prop changes. Score defaults to a hype "9" because the form
  // is now a "going" planner, not a scorecard — user can edit.
  useEffect(() => {
    if (!prefill || !edcMode) return;
    if (prefill.id) setTerritory(prefill.id);
    if (!score) setScore('9');
    if (!course || course === 'Emerald') setCourse('edc');
  }, [prefill, edcMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    const normalizedTerritory = territory.trim().toLowerCase();
    const normalizedBattleKey = battleKey.trim().toLowerCase();
    const normalizedScore = score.trim().toLowerCase();
  
    // In EDC mode the form is a "going" RSVP, so we send all the
    // metadata the Netlify edcSetClaim branch wants (set id, artist,
    // stage, day, start time). `key` is preserved for back-compat with
    // the Netlify body destructure; `userTag` is the canonical handle.
    const isEdc = edcMode || course === 'edc';
    const payload = isEdc
      ? {
          form: 'score',
          course: 'edc',
          key: normalizedBattleKey,
          userTag: normalizedBattleKey,
          setId: prefill?.id || normalizedTerritory,
          artist: prefill?.artist || matchedSet?.artist || '',
          stage: prefill?.stage || matchedSet?.stage || '',
          day: prefill?.day || matchedSet?.day || '',
          startTime: prefill?.start || matchedSet?.start || '',
          score: normalizedScore,
          territory: normalizedTerritory,
        }
      : {
          form: 'score',
          key: normalizedBattleKey,
          territory: normalizedTerritory,
          score: normalizedScore,
          course,
        };

    const res = await fetch('/.netlify/functions/submit-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  
    setLoading(false);
  
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      alert(error.error || 'Invalid key or failed submission');
      return;
    }
  
    const data = await res.json();
    console.log('✅ Submitted: new', data);
  
    if (onSuccess) onSuccess();
      if (remember) {
      localStorage.setItem('savedCourse', course);
      localStorage.setItem('savedBattleKey', normalizedBattleKey);
      localStorage.setItem('savedBattleKeyScope', isEdc ? 'edc' : 'golf');
    }
    setTerritory('');
    setScore('');
    setbattleKey('');
    setCourse('');
  };

  useEffect(() => {
    const savedCourseVal = localStorage.getItem('savedCourse');
    const savedBattleKeyVal = localStorage.getItem('savedBattleKey');
    // Older saves have no scope. Those came from the golf claim form.
    const savedScope = localStorage.getItem('savedBattleKeyScope') || 'golf';
    const scope = edcMode ? 'edc' : 'golf';
    const tagMatches = Boolean(savedBattleKeyVal) && savedScope === scope;

    if (edcMode) {
      setCourse('edc');
    } else if (savedCourseVal && savedCourseVal !== 'edc') {
      setCourse(savedCourseVal);
      setSavedCourse(savedCourseVal);
    }

    if (tagMatches) {
      setbattleKey(savedBattleKeyVal);
      setSavedBattleKey(savedBattleKeyVal);
    } else {
      setbattleKey('');
      setSavedBattleKey(null);
      if (savedBattleKeyVal) {
        localStorage.removeItem('savedBattleKey');
        localStorage.removeItem('savedBattleKeyScope');
      }
    }

    setHasSavedData(Boolean(!edcMode && savedCourseVal && tagMatches));
  }, [edcMode]);
  
  useEffect(() => {
    if (savedCourse && course && savedCourse !== course) {
      setHasSavedData(false); // re-show checkbox
    }
  }, [course, savedCourse]);

  return (
    
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '320px',
      }}
    > 
    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
      {edcMode ? (
        <Box sx={{ mt: '-2px' }}>
          <Typography
            className="edc-title"
            sx={{
              fontFamily: '"Staatliches", "Lexend Deca", sans-serif',
              fontSize: '0.75rem',
              letterSpacing: '0.12em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            HEADBANGER UNION LOCAL 808
          </Typography>
        </Box>
      ) : (
        <img
          src="https://images.ctfassets.net/zw84cuagwed8/657F89iQ3YSYAekJ9wtp5p/355a986267e9a9b92e1f4005f475ac27/g-med.png"
          alt="Battle for Muni"
          style={{
            maxWidth: '32px',
            height: 'auto',
            display: 'block',
            marginBottom: '10px',
            marginTop: '-7px',
            marginLeft: '0px'
          }}
        />
      )}
      <IconButton onClick={onCancel}>
        <CloseRoundedIcon />
      </IconButton>
    </Box>
      <CssBaseline enableColorScheme />
      <Typography variant="h4" sx={{textAlign: "left", letterSpacing: '0.00em'}} gutterBottom>
        {edcMode ? "I'm Going to This Set" : 'Claim Hole'}
      </Typography>

      {edcMode && matchedSet && (
        <Box
          className="edc-prefill-card"
          sx={{
            p: 1.5,
            mb: 1,
            border: '2px solid #ffd400',
            borderRadius: 1.5,
            background: 'rgba(155, 48, 255, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.4,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.7rem',
              letterSpacing: '0.16em',
              color: '#ffd400 !important',
              fontWeight: 700,
            }}
          >
            {getDayLabel(matchedSet.day)} · {formatSetTime(matchedSet.start)} ·{' '}
            {matchedSet.durationMin}m
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 800,
              color: '#ffffff !important',
              lineHeight: 1.1,
            }}
          >
            {matchedSet.artist}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.85) !important',
            }}
          >
            {matchedSet.stage} · {matchedSet.genre.toUpperCase()}
          </Typography>
        </Box>
      )}

      <FormLabel htmlFor="territory" sx={{mb:-1}}>
        {edcMode ? 'Set' : 'Territory'}
      </FormLabel>
      {edcMode ? (
        <Select
          className="form-input"
          id="territory"
          value={territory}
          variant="outlined"
          displayEmpty
          onChange={(e) => setTerritory(e.target.value)}
        >
          <MenuItem value="">
            <em>Pick a set</em>
          </MenuItem>
          {EDC_LINEUP.map((set) => (
            <MenuItem key={set.id} value={set.id}>
              {getDayLabel(set.day).split(' ')[0].slice(0, 3)}{' '}
              {formatSetTime(set.start)} — {set.artist} ({set.stage})
            </MenuItem>
          ))}
        </Select>
      ) : (
        <TextField
          fullWidth
          className="form-input"
          id="territory"
          placeholder="Hole Number"
          value={territory}
          onChange={(e) => setTerritory(e.target.value)}
          required
          sx={{
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'black',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'black',
            },
          }}
        />
      )}
      <FormLabel htmlFor="score" sx={{mb:-1}}>
        {edcMode ? 'Hype Level (1-10)' : 'Score'}
      </FormLabel>
      <TextField
        fullWidth
        className="form-input"
        id="score"
        value={score}
        placeholder={edcMode ? 'Ex. 9' : 'Ex. 8'}
        onChange={e => setScore(e.target.value)}
        required
        sx={{
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'black',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'black',
          },
        }}
      />
      <FormLabel htmlFor="battle-key" sx={{mb:-1}}>
        {edcMode ? 'Your Tag' : 'Battle Key'}
      </FormLabel>
      <TextField
        fullWidth
        className="form-input"
        name="battle key"
        id="battle-key"
        value={battleKey}
        placeholder={edcMode ? 'Ex. ravegoblin99' : 'Ex. bigidiot2'}
        type="text"
        onChange={e => setbattleKey(e.target.value)}
        required
        sx={{
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'black',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'black',
          },
        }}
      />
      <FormLabel htmlFor="battle-key" sx={{mb:-1}}>
        {edcMode ? 'Festival' : 'Course'}
      </FormLabel>
      <Select
        className="form-input"
        id="course"
        value={course}
        label={edcMode ? 'Festival' : 'Course'}
        placeholder={edcMode ? 'Festival' : 'Course'}
        variant='outlined'
        onChange={e => setCourse(e.target.value)}
      >
        {edcMode ? (
          <MenuItem value="edc">EDC Las Vegas 2026</MenuItem>
        ) : (
          [
            <MenuItem key="em" value="em">Emerald</MenuItem>,
            <MenuItem key="muni" value="muni">Muni</MenuItem>,
          ]
        )}
      </Select>

      {!hasSavedData && (
        <FormControlLabel
          control={
            <Checkbox
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              sx={{
                color: 'green',
                '&.Mui-checked': {
                  color: 'white',
                  backgroundColor: '#106a41',
                  borderRadius: '4px',
                  '& .MuiSvgIcon-root': {
                    fill: 'white',
                  },
                },
                '&.Mui-checked:hover': {
                  backgroundColor: '#228B22',
                },
              }}                          
            />
          }
          label={edcMode ? 'Remember my tag' : 'Save course details'}
        />
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button fullwidth variant="contained" type="submit">
          {loading ? (
            <CircularProgress size={16} sx={{ color: 'white !important' }} />
          ) : edcMode ? (
            "I'm Going!"
          ) : (
            'Submit'
          )}
        </Button>
      </Stack>
    {/*  <Divider>
        <Typography sx={{ color: 'text.secondary' }}>or</Typography>
      </Divider>
      <Box width="100%">
        <Button fullWidth variant="contained">
          Add Full Round
        </Button>
      </Box> */}
    </Box>
  );
}